import { useCallback, useRef, useState } from "react";
import { useToast } from "components/ui/Toast/use-toast";
import { logError } from "utility/Function";
import { CoachAuthError } from "apps/user/learner/api/coach";
import {
  ConversationDetailMessage,
  fetchConversation,
} from "apps/user/learner/api/coach-history";
import { authenticationFailed } from "providers/apollo-client";
import { Message } from "apps/user/learner/pages/chat/types";

function toMessages(detail: ConversationDetailMessage[]): Message[] {
  return [...detail]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((m) => ({
      role: m.role,
      content: m.content,
      message_id: m.id,
      embedded_ui: m.embedded_ui ?? undefined,
      user_feedback: m.user_feedback ?? null,
    }));
}

interface UseOriHistoryReturn {
  selectedDateKey: string | null;
  readOnlyMessages: Message[];
  readOnlyStartedAt: string | null;
  isLoadingConversation: boolean;
  selectConversation: (conversationIds: string[], dateKey: string) => Promise<void>;
  clearSelection: () => void;
}

export default function useOriHistory(): UseOriHistoryReturn {
  const { openToast } = useToast();
  // openToast is a fresh ref each render; pin it so the callbacks stay stable.
  const openToastRef = useRef(openToast);
  openToastRef.current = openToast;
  // Bumped per selection so a slow fetch for an earlier pick can't overwrite a
  // later one (rapid re-select / double-click).
  const requestRef = useRef(0);

  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [readOnlyMessages, setReadOnlyMessages] = useState<Message[]>([]);
  const [readOnlyStartedAt, setReadOnlyStartedAt] = useState<string | null>(
    null
  );
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  const clearSelection = useCallback(() => {
    requestRef.current += 1;
    setSelectedDateKey(null);
    setReadOnlyMessages([]);
    setReadOnlyStartedAt(null);
    setIsLoadingConversation(false);
  }, []);

  // A calendar day can span several Conversation rows (a mode switch, or a
  // completed conversation restarting later the same day never resumes the
  // old row — see conversation_lifecycle_service.py) so every segment for the
  // day is fetched and stitched into one continuous transcript, the same way
  // the live chat view renders it.
  const selectConversation = useCallback(
    async (conversationIds: string[], dateKey: string) => {
      const requestId = (requestRef.current += 1);
      setSelectedDateKey(dateKey);
      setReadOnlyMessages([]);
      setReadOnlyStartedAt(null);
      setIsLoadingConversation(true);
      try {
        const details = await Promise.all(
          conversationIds.map((id) => fetchConversation(id))
        );
        if (requestRef.current !== requestId) return;
        setReadOnlyMessages(toMessages(details.flatMap((d) => d.messages)));
        setReadOnlyStartedAt(details[0]?.started_at ?? null);
      } catch (error) {
        if (requestRef.current !== requestId) return;
        if (error instanceof CoachAuthError) {
          authenticationFailed();
          clearSelection();
          return;
        }
        logError("Ori conversation fetch failed", error);
        openToastRef.current({
          variant: "error",
          message: "Couldn't load that conversation",
        });
        clearSelection();
      } finally {
        if (requestRef.current === requestId) setIsLoadingConversation(false);
      }
    },
    [clearSelection]
  );

  return {
    selectedDateKey,
    readOnlyMessages,
    readOnlyStartedAt,
    isLoadingConversation,
    selectConversation,
    clearSelection,
  };
}
